/* /biometric — Devices: KPIs, pending devices (claim), device list, connect wizard, explainer. */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Device } from "../../data/types";
import { useBiometricOverview, useClock, useDeleteDevice, useDevices, useSyncDevice, useSyncEmployeesToDevice } from "../../data/hooks";
import { Alert, Button, Card, Chip, Confirm, EmptyState, ErrorBox, IconButton, Kpi, PageHeader, Skeleton, errMsg, useToast } from "../../ui";
import { useI18n } from "../../i18n";
import { fmtN } from "../../lib/format";
import { ConnectDeviceWizard } from "./components/ConnectDeviceWizard";
import { ClaimModal, DeviceStatus, EditDeviceModal } from "./components/DeviceBits";
import { HowItWorks } from "./components/HowItWorks";
import { useDemoPunch } from "./components/demo";
import { hm, protocolLabel } from "./components/labels";
import "./time.css";

export function syncToast(d: Device, accepted: number, L: (en: string, ar: string) => string): string {
  if (d.protocol === "push") return L("Log request queued — the device uploads today's punches at its next check-in", "تمت جدولة طلب السجلات — سيرفع الجهاز بصمات اليوم عند اتصاله التالي");
  if (d.protocol === "poll" || d.protocol === "agent") return L(`Synced — ${accepted} new punches`, `تمت المزامنة — ${accepted} بصمة جديدة`);
  return L("Nothing to sync — this device sends data by itself", "لا حاجة للمزامنة — يرسل الجهاز البيانات تلقائيًا");
}

export default function Biometric() {
  const { L, t, branch } = useI18n();
  const toast = useToast();
  const nav = useNavigate();
  const { now } = useClock();
  const ov = useBiometricOverview();
  const devs = useDevices();
  const sync = useSyncDevice();
  const pushUsers = useSyncEmployeesToDevice();
  const del = useDeleteDevice();
  const demo = useDemoPunch();
  const [wizard, setWizard] = useState(false);
  const [claim, setClaim] = useState<Device | null>(null);
  const [edit, setEdit] = useState<Device | null>(null);
  const [delDev, setDelDev] = useState<Device | null>(null);
  const [howOpen, setHowOpen] = useState<boolean | null>(null);

  if (devs.error) return <><PageHeader title={L("Biometric devices", "أجهزة البصمة")} /><ErrorBox error={devs.error} onRetry={() => devs.refetch()} /></>;
  if (!devs.data) return <Skeleton />;
  const all = devs.data;
  const pending = all.filter((d) => !d.claimed);
  const mine = all.filter((d) => d.claimed);
  const o = ov.data;

  const doSync = (d: Device) => sync.mutate(d.id, { onSuccess: (r) => toast(syncToast(d, r.accepted, L)), onError: (e) => toast(errMsg(e), "bad") });
  const doPush = (d: Device) => pushUsers.mutate({ deviceId: d.id }, { onSuccess: (r) => toast(L(`${r.length} employees queued for ${d.name}`, `تمت جدولة ${r.length} موظفًا للجهاز ${d.name}`)), onError: (e) => toast(errMsg(e), "bad") });
  const doDelete = () => {
    if (!delDev) return;
    const d = delDev;
    del.mutate(d.id, { onSuccess: () => { toast(L("Device removed — its punches are kept", "تم حذف الجهاز — البصمات محفوظة")); setDelDev(null); }, onError: (e) => toast(errMsg(e), "bad") });
  };
  const simulateNew = async () => {
    const sn = "CQZ" + String(Date.now()).slice(-10);
    try { await demo.handshake(sn); toast(L(`A device with serial ${sn} contacted StartHR`, `اتصل جهاز برقم ${sn} بستارت HR`)); } catch (e) { toast(errMsg(e), "bad"); }
  };

  return (
    <div data-testid="biometric-page">
      <PageHeader title={L("Biometric devices", "أجهزة البصمة")}
        sub={L("Face, fingerprint and card terminals send punches straight to StartHR — attendance fills itself.", "ترسل أجهزة الوجه والبصمة والبطاقة الحركات مباشرة إلى ستارت HR — ويُعبّأ الحضور تلقائيًا.")}
        actions={<>
          <Link className="btn" to="/biometric/punches" data-testid="link-punches">{L("Punch log", "سجل البصمات")}</Link>
          <Link className="btn" to="/attendance">{t("Attendance")}</Link>
          <Button variant="pri" icon="plus" onClick={() => setWizard(true)} data-testid="btn-connect">{L("Connect a device", "ربط جهاز")}</Button>
        </>} />

      <div className="kpi5 mb" data-testid="bio-kpis">
        <Kpi label={L("Devices", "الأجهزة")} value={o ? String(o.devices) : "—"} icon="fingerprint" testId="kpi-devices" />
        <Kpi label={L("Online", "متصل")} value={o ? String(o.online) : "—"} icon="wifi" tone="ok" testId="kpi-online" />
        <Kpi label={L("Offline", "غير متصل")} value={o ? String(o.offline) : "—"} icon="wifi-off" tone={o && o.offline ? "bad" : undefined} testId="kpi-offline" />
        <Kpi label={L("Punches today", "بصمات اليوم")} value={o ? fmtN(o.punchesToday, 0) : "—"} icon="clock" tone="info" foot={o?.lastPunchAt ? L("last at ", "آخرها ") + hm(o.lastPunchAt) : undefined} testId="kpi-punches" />
        <Kpi label={L("Unmatched users", "مستخدمون غير مرتبطين")} value={o ? String(o.unmatched) : "—"} icon="user" tone={o && o.unmatched ? "warn" : undefined} to="/biometric/unmatched" foot={L("Link to employees →", "اربطهم بالموظفين ←")} testId="kpi-unmatched" />
      </div>

      {pending.map((d) => (
        <div className="mb" key={d.id}>
          <Alert tone="warn" testId={"pending-" + d.id}
            title={L(`A device with serial ${d.serial} is trying to connect`, `جهاز بالرقم التسلسلي ${d.serial} يحاول الاتصال`)}
            desc={L(`${d.model || "ZKTeco"} · first contact ${d.lastSeen ? hm(d.lastSeen) : "—"} · IP ${d.ip || "—"}. Claim it to start recording punches.`, `${d.model || "ZKTeco"} · أول اتصال ${d.lastSeen ? hm(d.lastSeen) : "—"} · IP ${d.ip || "—"}. اعتمده لبدء تسجيل البصمات.`)}
            action={<Button variant="pri sm" onClick={() => setClaim(d)} data-testid={"btn-claim-" + d.id}>{L("Claim", "اعتماد")}</Button>} />
        </div>
      ))}

      <div className="mb"><HowItWorks key={String(howOpen ?? mine.length === 0)} open={howOpen ?? mine.length === 0} /></div>

      <Card title={L("Connected devices", "الأجهزة المرتبطة")} flush testId="devices-card"
        actions={demo.enabled ? <Button variant="ghost sm" icon="play" onClick={simulateNew} data-testid="btn-demo-new-device">{L("Simulate a new device (demo)", "محاكاة جهاز جديد (عرض)")}</Button> : undefined}>
        {mine.length === 0 ? (
          <EmptyState title={L("Connect your first device", "اربط جهازك الأول")}
            desc={L("Most devices connect in about 5 minutes — no IT visit, static IP or PC needed.", "تُربط معظم الأجهزة خلال 5 دقائق تقريبًا — دون زيارة فنية أو عنوان ثابت أو حاسب.")}
            action={<div className="row" style={{ justifyContent: "center", marginTop: 12 }}>
              <Button variant="pri" icon="plus" onClick={() => setWizard(true)} data-testid="btn-connect-empty">{L("Connect your first device", "اربط جهازك الأول")}</Button>
              <Button icon="info" onClick={() => setHowOpen(true)} data-testid="btn-how">{L("How it works", "كيف يعمل")}</Button>
            </div>} />
        ) : (
          <div className="tw"><table className="tbl cards" data-testid="devices-table">
            <thead><tr><th>{L("Device", "الجهاز")}</th><th className="hide-md">{t("Branch")}</th><th>{L("Method", "الطريقة")}</th><th>{t("Status")}</th><th className="hide-md">{L("Last punch", "آخر بصمة")}</th><th className="end hide-md">{L("Users", "المستخدمون")}</th><th className="end hide-md">{L("Punches", "البصمات")}</th><th className="end">{t("Actions")}</th></tr></thead>
            <tbody>{mine.map((d) => (
              <tr key={d.id} data-testid={"device-" + d.id}>
                <td className="k1"><Link to={"/biometric/devices/" + d.id} className="nm" data-testid={"open-" + d.id}><b>{d.name}</b></Link><div className="xs muted">{d.vendor} · {d.model || "—"}</div></td>
                <td className="hide-md x" data-l={t("Branch")}>{d.branch ? branch(d.branch) : "—"}</td>
                <td className="x" data-l={L("Method", "الطريقة")}><Chip tone="info" nodot>{protocolLabel(d.protocol, L)}</Chip></td>
                <td className="k2"><DeviceStatus d={d} now={now} /></td>
                <td className="hide-md num sm">{d.lastPunchAt ? d.lastPunchAt.slice(0, 10) + " " + hm(d.lastPunchAt) : "—"}</td>
                <td className="end hide-md num">{d.userCount}</td>
                <td className="end hide-md num">{fmtN(d.punchCount, 0)}</td>
                <td className="end k3" data-l="">
                  <div className="row nw" style={{ justifyContent: "flex-end", gap: 4 }}>
                    {(d.protocol === "push" || d.protocol === "poll" || d.protocol === "agent") && <IconButton icon="refresh" label={L("Sync now", "مزامنة الآن") + " · " + d.name} onClick={() => doSync(d)} data-testid={"btn-sync-" + d.id} />}
                    {(d.protocol === "push" || d.protocol === "agent") && <IconButton icon="users" label={L("Push employees", "إرسال الموظفين") + " · " + d.name} onClick={() => doPush(d)} data-testid={"btn-push-" + d.id} />}
                    <IconButton icon="layers" label={L("Commands", "الأوامر") + " · " + d.name} onClick={() => nav("/biometric/devices/" + d.id + "?tab=commands")} data-testid={"btn-cmds-" + d.id} />
                    <IconButton icon="edit" label={t("Edit") + " · " + d.name} onClick={() => setEdit(d)} data-testid={"btn-edit-" + d.id} />
                    <IconButton icon="trash" label={L("Delete", "حذف") + " · " + d.name} onClick={() => setDelDev(d)} data-testid={"btn-delete-" + d.id} />
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>

      {wizard && <ConnectDeviceWizard onClose={() => setWizard(false)} onDone={(d) => { setWizard(false); nav("/biometric/devices/" + d.id); }} />}
      {claim && <ClaimModal device={claim} onClose={() => setClaim(null)} />}
      {edit && <EditDeviceModal device={edit} onClose={() => setEdit(null)} />}
      {delDev && <Confirm title={L("Delete this device?", "حذف هذا الجهاز؟")} danger busy={del.isPending} confirmLabel={L("Delete device", "حذف الجهاز")} onClose={() => setDelDev(null)} onConfirm={doDelete}
        body={<p style={{ margin: 0 }}>{L(`“${delDev.name}” will stop sending punches to StartHR. Punches already received stay in attendance.`, `سيتوقف «${delDev.name}» عن إرسال البصمات إلى ستارت HR. البصمات المستلمة تبقى في الحضور.`)}</p>} />}
    </div>
  );
}
